import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color options in required order
const COLOR_OPTIONS = [
  { value: "Green / Blue", label: "G/B", colors: ["Green", "Blue"] },
  { value: "Yellow", label: "Y", colors: ["Yellow"] },
  { value: "Yellow / Blue", label: "Y/B", colors: ["Yellow", "Blue"] },
  { value: "Red", label: "R", colors: ["Red"] },
  { value: "Red / Blue", label: "R/B", colors: ["Red", "Blue"] },
  { value: "Blue", label: "B", colors: ["Blue"] },
];

// All swim levels
const SWIM_LEVELS = ["PSA1", "PSA2", "1", "2", "3", "4", "5", "6"];

// Test definitions for each color
const TEST_DEFINITIONS = {
  Yellow: [
    "Vinyl pool swim 1 length",
    "Tread water for 30 seconds",
    "Underwater / Breath",
  ],
  Red: [
    "In deep end, jump in and swim 2 laps",
    "Tread for 30 seconds",
    "Float on back for 30 seconds",
  ],
  Blue: [
    "One lap back and forth in main pool with life jacket - swim front crawl",
    "With life jacket swim back and forth front crawl",
    "Deep end - tread for 30 seconds, float on back for 30 seconds with life jacket",
  ],
  Green: [
    "Basic water safety assessment",
    "Pool entry and exit",
    "Basic floating",
  ],
};

// Get today's date in EST timezone as YYYY-MM-DD
const getTodayEST = () => {
  const now = new Date();
  const estDate = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const year = estDate.getFullYear();
  const month = String(estDate.getMonth() + 1).padStart(2, "0");
  const day = String(estDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function InlineCamperRow({ camper, lifeguard, onUpdate, onDelete, onClick }) {
  const [savingColor, setSavingColor] = useState(false);
  const [savingLevel, setSavingLevel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Handle delete camper
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/campers/${camper.id}`);
      toast.success(`Deleted ${camper.first_name} ${camper.last_name}`);
      if (onDelete) onDelete(camper.id);
    } catch (error) {
      toast.error("Failed to delete camper");
      console.error(error);
    }
    setDeleting(false);
  };

  // Get color badge styling
  const getColorBadgeClass = (swimColor) => {
    if (!swimColor || swimColor === "-") return "bg-gray-100 text-gray-600";
    const c = swimColor.toLowerCase();
    if (c === "green") return "bg-green-100 text-green-700";
    if (c === "yellow") return "bg-yellow-100 text-yellow-700";
    if (c === "red") return "bg-red-100 text-red-700";
    if (c === "blue") return "bg-blue-100 text-blue-700";
    if (c.includes("green") && c.includes("blue")) return "bg-gradient-to-r from-green-100 to-blue-100 text-green-700";
    if (c.includes("yellow") && c.includes("blue")) return "bg-gradient-to-r from-yellow-100 to-blue-100 text-yellow-700";
    if (c.includes("red") && c.includes("blue")) return "bg-gradient-to-r from-red-100 to-blue-100 text-red-700";
    return "bg-gray-100 text-gray-600";
  };

  // Get short label for current color
  const getColorLabel = (swimColor) => {
    if (!swimColor || swimColor === "-") return "-";
    const c = swimColor.toLowerCase();
    if (c === "green") return "G";
    if (c === "yellow") return "Y";
    if (c === "red") return "R";
    if (c === "blue") return "B";
    if (c.includes("green") && c.includes("blue")) return "G/B";
    if (c.includes("yellow") && c.includes("blue")) return "Y/B";
    if (c.includes("red") && c.includes("blue")) return "R/B";
    return swimColor.charAt(0).toUpperCase();
  };

  // Handle color selection - auto-save with all tests passed
  const handleColorSelect = async (selectedValue) => {
    // Default to "Dina Testa" if no lifeguard selected
    const instructorName = lifeguard || "Dina Testa";

    const colorOption = COLOR_OPTIONS.find(opt => opt.value === selectedValue);
    if (!colorOption) return;

    setSavingColor(true);
    
    try {
      // Record test results for each color in the selection
      for (const color of colorOption.colors) {
        const testDescriptions = TEST_DEFINITIONS[color] || [];
        const tests = testDescriptions.map((desc) => ({
          description: desc,
          yes_no: "yes",
          pass_fail: "pass",
        }));

        await axios.post(`${API}/test-results`, {
          camper_id: camper.id,
          swim_color: color,
          tests,
          instructor_name: instructorName,
          date_passed: getTodayEST(),
        });
      }

      toast.success(`${colorOption.label} saved!`, {
        description: `${camper.first_name} passed ${colorOption.colors.join(" & ")}`,
      });

      // Fetch updated camper data
      const response = await axios.get(`${API}/campers/${camper.id}`);
      onUpdate(response.data);
    } catch (error) {
      console.error("Error saving color:", error);
      toast.error("Failed to save color", {
        description: error.response?.data?.detail || "Please try again",
      });
    } finally {
      setSavingColor(false);
    }
  };

  // Handle level selection - PATCH camper
  const handleLevelSelect = async (level) => {
    setSavingLevel(true);
    
    try {
      await axios.patch(`${API}/campers/${camper.id}`, {
        swim_level: level,
      });

      toast.success(`Level updated to ${level}`);

      // Fetch updated camper data
      const response = await axios.get(`${API}/campers/${camper.id}`);
      onUpdate(response.data);
    } catch (error) {
      console.error("Error saving level:", error);
      toast.error("Failed to update level");
    } finally {
      setSavingLevel(false);
    }
  };

  return (
    <div
      className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 hover:shadow-md transition-all"
      data-testid={`inline-camper-row-${camper.id}`}
    >
      {/* Top Row: Name + Clickable area */}
      <div 
        className="flex items-center gap-2 cursor-pointer mb-2"
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onClick()}
      >
        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <User className="h-4 w-4 text-gray-500" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-800 text-sm truncate">
            {camper.full_name || `${camper.first_name} ${camper.last_name}`}
          </h3>
        </div>
        {/* Current color badge */}
        <div className={cn(
          "px-2 py-1 rounded-full text-xs font-bold min-w-[32px] text-center",
          getColorBadgeClass(camper.swim_color)
        )}>
          {getColorLabel(camper.swim_color)}
        </div>
      </div>

      {/* Bottom Row: Dropdowns */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
        {/* Color Dropdown */}
        <div className="flex-1">
          <Select
            value=""
            onValueChange={handleColorSelect}
            disabled={savingColor}
          >
            <SelectTrigger 
              className={cn(
                "h-9 text-xs border-gray-200 text-black",
                savingColor && "opacity-50"
              )}
              data-testid={`color-dropdown-${camper.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue placeholder={savingColor ? "Saving..." : "Set Color"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {COLOR_OPTIONS.map((opt) => (
                <SelectItem 
                  key={opt.value} 
                  value={opt.value}
                  className="text-sm text-black hover:bg-gray-100"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-gray-500 ml-2">({opt.colors.join(" + ")})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Level Dropdown */}
        <div className="flex-1">
          <Select
            value={camper.swim_level || ""}
            onValueChange={handleLevelSelect}
            disabled={savingLevel}
          >
            <SelectTrigger 
              className={cn(
                "h-9 text-xs border-gray-200 text-black",
                savingLevel && "opacity-50"
              )}
              data-testid={`level-dropdown-${camper.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue placeholder={savingLevel ? "Saving..." : "Level"} />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              <SelectItem value="-" className="text-sm text-black hover:bg-gray-100">
                No Level
              </SelectItem>
              {SWIM_LEVELS.map((level) => (
                <SelectItem 
                  key={level} 
                  value={level}
                  className="text-sm text-black hover:bg-gray-100"
                >
                  {level.startsWith("PSA") ? `PSA ${level.replace("PSA", "")}` : `Level ${level}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Delete Button */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="h-9 w-9 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
              data-testid={`delete-btn-${camper.id}`}
              title="Delete camper"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-white border-gray-200">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-black">Delete {camper.first_name} {camper.last_name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-600">
                This will permanently delete this camper and all their test results. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-gray-100 border-gray-300 text-black hover:bg-gray-200">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
